import { InventoryPerformanceReport } from "../../inventoryPerformance/InventoryPerformanceTableReport.component";
import CustomReportService from "../../../../services/reports/CustomReport.service";
import { MoneyDataItem } from "../../../../interfaces/reports/BaseReport.interface";
import { useState } from "react";
import ReportService from "../../../../services/reports/Report.service";

interface TootsiesInventoryPerformanceProps {
  clientId: string | number;
  epochMonth: number | null;
}

const brandHierarchyInv: Record<string, string[]> = {
    "By Brand": ["brand"],
    "Location by Brand": ["locationCode", "brand"],
    "Location by Class by Brand": ["locationCode", "classCode", "brand"],
    "Class by Brand": ["classCode", "brand"],
    "Class by Brand by Location": ["classCode", "brand", "locationCode"],
    "Category by Class by Brand": ["category", "classCode", "brand"],
    "Category by Brand": ["category", "brand"],
    "Category by Location by Brand": ["category", "locationCode", "brand"],
    "Location by Category by Brand": ["locationCode", "category", "brand"],
    "Category by Brand by Location": ["category", "brand", "locationCode"],
    "By Location": ["locationCode"],
    "Location By Class": ["locationCode", "classCode"],
    "Location By Category": ["locationCode", "category"],
    "Location by Category by Class": ["locationCode", "category", "classCode"],
    "By Class": ["classCode"],
    "Class by Location": ["classCode", "locationCode"],
    "By Category": ["category"],
    "Category by Location": ["category", "locationCode"],
    "Category by Class": ["category", "classCode"],
    "Category by Class by Location": ["category", "classCode", "locationCode"],
};

const TootsiesInventoryPerformanceReport = ({ clientId, epochMonth }: TootsiesInventoryPerformanceProps) => {
  const [showOnlyActuals, setShowOnlyActuals] = useState<boolean>(true);

  // Parse function override to filter brands by actuals
  const parseDataFn = (raw: any[]) => {
    if (!Array.isArray(raw) || raw.length === 0) return [];
    const filtered = raw.filter((r) => {
      if (!showOnlyActuals) return true;
      const sales = Number(r?.sales ?? r?.salesLm ?? 0);
      const markdowns = Number(r?.markdowns ?? r?.markdownsL3 ?? 0);
      const inventory = Number(r?.inventory ?? r?.invCurrent ?? r?.invAvg ?? r?.invBomRm ?? 0);
      const receiving = Number(r?.inflowRetail ?? r?.inflowCost ?? 0);
      return (sales > 0) || (markdowns > 0) || (inventory > 0) || (receiving > 0);
    });

    return filtered.map((r) => ({
      locationCode: r.locationCode,
      locationName: r.locationName,
      classCode: r.classCode,
      className: r.className,
      parentClientClassCode: r.parentClientClassCode ?? null,
      category: r.category ?? 'N/A',
      categorySortOrder: r.categorySortOrder ?? null,
      brand: r.brand ?? 'Unknown',
      sales: new MoneyDataItem(r.sales ?? 0),
      markdowns: new MoneyDataItem(r.markdowns ?? 0),
      inflowCost: new MoneyDataItem(r.inflowCost ?? 0),
      inflowRetail: new MoneyDataItem(r.inflowRetail ?? 0),
      invAvg: new MoneyDataItem(r.invAvg ?? 0),
      invAvgCost: new MoneyDataItem(r.invAvgCost ?? 0),
      invBomRm: new MoneyDataItem(r.invBomRm ?? 0),
      invCurrent: new MoneyDataItem(r.invCurrent ?? 0),
      imu: Number(r.imu ?? 0),
      costComplement: Number(r.costComplement ?? 0),
      cashMargin: new MoneyDataItem(ReportService.calculateCashMargin({ sales: r.sales ?? 0, inflowCost: r.inflowCost ?? 0, format: false })),
      grossMargin: new MoneyDataItem(ReportService.calculateGrossMargin({ sales: r.sales ?? 0, markdowns: r.markdowns ?? 0, costComplement: r.costComplement ?? 0, imu: r.imu ?? 0, format: false })),
      CMROI: "",
      GMROI: "",
      turn: "",
      efficiency: "",
      freshness: "",
    }));
  };

  const fetchDataFn = async ({ clientId, startEpochMonth, endEpochMonth, isMtd }: { clientId: string | number; startEpochMonth: number; endEpochMonth: number; isMtd: boolean }) => {
    return await CustomReportService.getTootsiesInventoryPerformance({ clientId, startEpochMonth, endEpochMonth, isMtd });
  };

  const extraControls = (
    <div className="flex flex-col items-start">
      <span className="text-xs font-semibold text-slate-600">Brands</span>
      <select
        value={showOnlyActuals ? 'actuals' : 'all'}
        onChange={e => setShowOnlyActuals(e.target.value === 'actuals')}
        className="h-10 px-4 border border-[#e1edf1] rounded-md text-[13px] font-medium text-gray-700 focus:outline-none focus:ring-2 focus:ring-sky-200"
      >
        <option value="actuals">Only with actuals</option>
        <option value="all">All brands</option>
      </select>
    </div>
  );

  return (
    <div className="w-full h-full flex flex-col items-center justify-center text-center">
      <InventoryPerformanceReport
        key={showOnlyActuals ? 'actuals' : 'all'}
        clientId={clientId}
        epochMonth={epochMonth}
        hierarchyOverride={brandHierarchyInv}
        fetchDataFn={fetchDataFn as any}
        parseDataFn={parseDataFn as any}
        extraControls={extraControls}
      />
    </div>
  );
};

export default TootsiesInventoryPerformanceReport;